import i18n from "@/i18n/i18n";
import { User } from "@/types/auth";

export enum UserRole {
  CRAVE_ADMIN = "CRAVE_ADMIN",
  CRAVE_PRIVILEGE = "CRAVE_PRIVILEGE",
  CRAVE_EMPLOYEE = "CRAVE_EMPLOYEE",
  CUSTOMER_ADMIN = "CUSTOMER_ADMIN",
  CUSTOMER = "CUSTOMER",
  STORE_ADMIN = "STORE_ADMIN",
  STORE = "STORE",
  ASSOCIATE = "ASSOCIATE",
  SHOPPER = "SHOPPER",
  ONLINE_SHOPPER = "ONLINE_SHOPPER",
  NONE = "NONE",
}

interface RoleInfo {
  level: number;
  displayName: string;
  translationKey?: string;
}

const RoleData: Record<UserRole, RoleInfo> = {
  [UserRole.CRAVE_ADMIN]: {
    level: 99999,
    displayName: "Crave Admin",
    translationKey: "settings.userManagement.roles.craveAdmin",
  },
  [UserRole.CRAVE_PRIVILEGE]: {
    level: 55555,
    displayName: "Crave Privilege",
    translationKey: "settings.userManagement.roles.cravePrivilege",
  },
  [UserRole.CRAVE_EMPLOYEE]: {
    level: 10000,
    displayName: "Crave Employee",
    translationKey: "settings.userManagement.roles.craveEmployee",
  },
  [UserRole.CUSTOMER_ADMIN]: {
    level: 500,
    displayName: "Customer Admin",
    translationKey: "settings.userManagement.roles.customerAdmin",
  },
  [UserRole.CUSTOMER]: {
    level: 400,
    displayName: "Customer",
    translationKey: "settings.userManagement.roles.customer",
  },
  [UserRole.STORE_ADMIN]: {
    level: 300,
    displayName: "Store Manager",
    translationKey: "settings.userManagement.roles.storeManager",
  },
  [UserRole.STORE]: {
    level: 200,
    displayName: "Store",
    translationKey: "settings.userManagement.roles.store",
  },
  [UserRole.ASSOCIATE]: {
    level: 100,
    displayName: "Associate",
    translationKey: "settings.userManagement.roles.associate",
  },
  [UserRole.SHOPPER]: {
    level: 20,
    displayName: "Shopper",
    translationKey: "settings.userManagement.roles.shopper",
  },
  [UserRole.ONLINE_SHOPPER]: {
    level: 5,
    displayName: "Online Shopper",
    translationKey: "settings.userManagement.roles.onlineShopper",
  },
  [UserRole.NONE]: {
    level: 0,
    displayName: "None",
    translationKey: "settings.userManagement.roles.none",
  },
};

export const ActiveRoles = [UserRole.CUSTOMER_ADMIN, UserRole.STORE_ADMIN, UserRole.ASSOCIATE];

export const getRoleDisplayName = (role: string): string => {
  const roleInfo = RoleData[role as UserRole];
  if (!roleInfo) {
    return role;
  }

  return roleInfo.translationKey
    ? i18n.t(roleInfo.translationKey, { defaultValue: roleInfo.displayName })
    : roleInfo.displayName;
};

export const getRoleApiValue = (role: string): string => {
  const matchedRole = Object.entries(RoleData).find(([, roleInfo]) => {
    const localizedRole = roleInfo.translationKey
      ? i18n.t(roleInfo.translationKey, { defaultValue: roleInfo.displayName })
      : roleInfo.displayName;

    return roleInfo.displayName === role || localizedRole === role;
  })?.[0];
  return matchedRole || role;
};

const getRoleLevel = (role: string): number => {
  return RoleData[role as UserRole]?.level || 0;
};

export const isRoleAtLeast = (userRole: string, minimumRole: UserRole): boolean => {
  const userLevel = getRoleLevel(userRole);
  const minimumLevel = RoleData[minimumRole]?.level;
  return userLevel >= minimumLevel;
};

export const isUserRoleAtLeast = (user: User, minimumRole: UserRole): boolean => {
  return isRoleAtLeast(user.role, minimumRole);
};
